import * as React from 'react';
import { useContext } from 'react';
import { AppContext } from '../../context/AppContext';

/**
 * how loud a message is. 'error' is the only one that interrupts a screen
 * reader, the other two wait for a pause
 */
export type InlineMessageKind = 'success' | 'error' | 'info';

/**
 * what a section reports back to the user. the text is already translated by
 * the caller, because only the caller knows the wording and I18N.t(text, args)
 * crashes in this project
 */
export interface InlineMessageState {
  kind: InlineMessageKind;
  text: string;
}

interface InlineMessageProps {
  /**
   * null means "nothing to say right now" - the regions stay mounted anyway
   */
  message: InlineMessageState | null;
  /**
   * distinguishes the regions of several sections on the same screen, purely
   * for styling and for the test id
   */
  section: string;
  /**
   * only offered for errors, and only when the caller can really retry
   */
  onRetry?: () => void;
  /**
   * translated label of the retry button, defaults to "Erneut versuchen"
   */
  retryLabel?: string;
}

/**
 * InlineMessage
 *
 * the project has neither a toast nor a modal system, and the Alert component of
 * the bundle renders a dia-loader-overlay without any semantics. so success and
 * failure are reported exactly where DataTable reports them: inline, with a live
 * region, an own data attribute and text that went through I18N.t.
 *
 * both regions are rendered from the first paint on and only their text toggles.
 * a live region that is inserted into the dom together with its content is
 * usually not announced at all, which is the whole reason this component exists
 * instead of a conditional <p role="alert">.
 *
 * errors get role="alert" (assertive), success and info get role="status"
 * (polite): a saved password must not cut into whatever the user is reading,
 * a failed one must.
 */
const InlineMessage: React.FC<InlineMessageProps> = ({ message, section, onRetry, retryLabel }) => {
  const { I18N } = useContext(AppContext);

  const isError = !!message && message.kind === 'error',
    isNotice = !!message && !isError;

  return (
    <>
      <div
        data-inline-message
        data-inline-message-section={ section }
        data-inline-message-kind="error"
        role="alert"
      >
        { isError && (
          <>
            <p data-inline-message-text>{ message?.text }</p>
            { onRetry && (
              <button
                type="button"
                data-secondary
                data-small
                data-inline-message-retry
                onClick={ onRetry }
              >
                { retryLabel || I18N.t("Erneut versuchen") }
              </button>
            ) }
          </>
        ) }
      </div>
      <div
        data-inline-message
        data-inline-message-section={ section }
        data-inline-message-kind={ isNotice ? message?.kind : 'success' }
        role="status"
      >
        { isNotice && <p data-inline-message-text>{ message?.text }</p> }
      </div>
    </>
  );
};

export default InlineMessage;
